import type { CSSProperties, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { BasicValue, IconList, InputSize } from '@snowit/icetypes';
import type { A11yEvent, InputSelectOnChange } from '@snowit/icetypes/events';

import useA11y from '@snowit/hooks/useA11y';
import useBreakpoints from '@snowit/hooks/useBreakpoints';
import Portal from '@snowit/hooks/usePortals';
import { Keyboard } from '@snowit/iceconst';
import testId from '@snowit/utils/testId';

import ButtonIcon from '../../Buttons/ButtonIcon';
import Icon from '../../Icon';
import Typo from '../../Typography/Typo';
import { m } from '../../utils/Motion';
import Input from '../Input';

import cx from 'classnames';
import s from './InputSelect.module.scss';

export interface IInputOption<T extends any = any> {
  key: string;
  icon?: IconList;
  label?: string;
  hint?: string | string[];
  content?: string | ReactNode | (() => string | ReactNode);
  value?: BasicValue;
  valueSecondary?: string;
  notHover?: boolean;
  notBorder?: boolean;
  noCta?: boolean;
  disabled?: boolean;
  test?: string;
  entity?: T;
  notTabIndex?: boolean;
  hidden?: boolean;
}

export interface IInputSelect<T extends any = any> {
  size?: InputSize;
  label?: string;
  placeholder?: string;
  hint?: string;
  icon?: IconList;
  emoji?: boolean;
  value?: string | number | undefined | null;
  valueLabel?: (opt: IInputOption<T>) => string;
  valueSecondary?: (opt: IInputOption<T>) => string;
  options: (IInputOption<T> | string)[];
  name?: string;
  onChange?: (params: InputSelectOnChange<T>) => void;
  loading?: boolean;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorMsg?: string;
  id?: string;
  classNames?: {
    wrapper?: string;
    wrapInput?: string;
    input?: string;
    dropdown?: string;
    bodyDropdown?: string;
    option?: string;
  };
  /** Jest/Cypress test string */
  test?: string;
}

const InputSelect = <T extends any>(props: IInputSelect<T>) => {
  const {
    size,
    label,
    placeholder,
    hint,
    icon,
    emoji = false,
    valueLabel,
    valueSecondary,
    name,
    onChange,
    loading,
    required,
    readOnly,
    disabled,
    error,
    errorMsg,
    id,
    classNames,
    test,
  } = props;
  const { minWidth } = useBreakpoints();
  const a11yH = useA11y();

  const dropdownId = a11yH.makeId(props.id, 'dropdown');

  const value = String(props.value ?? '');

  const [wParent, setWParent] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const parentRef = useRef<HTMLInputElement>(null);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);

  const focusParent = () => {
    (parentRef.current as HTMLElement)?.focus();
  };

  const options: IInputOption<T>[] = props.options.map((opt) => {
    if (typeof opt === 'string') {
      return {
        key: opt,
        value: opt,
        label: opt,
      };
    }
    return opt;
  });

  const navigableKeys = useMemo(() => {
    return options
      .filter((option) => !option.notTabIndex)
      .filter((option) => !option.disabled)
      .map((option) => option.key);
  }, [options]);

  useEffect(() => {
    if (itemsRef.current instanceof Element) {
      itemsRef.current.scrollIntoView();
    }
  }, [itemsRef.current, showDropdown]);

  useEffect(() => {
    const index = navigableKeys[activeIndex];
    if (itemsRef.current[index]) {
      itemsRef.current[index].focus();
    }
  }, [activeIndex, itemsRef.current, navigableKeys]);

  useLayoutEffect(() => {
    if (parentRef.current instanceof HTMLElement) {
      setWParent(parentRef.current.offsetWidth);
    }
  }, []);

  const handleOpen = () => {
    setShowDropdown((prev) => {
      const next = !prev;
      setActiveIndex(next ? 0 : -1);
      return next;
    });
  };

  const handleClose = (
    event: A11yEvent<HTMLDivElement | HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    event.stopPropagation();
    setShowDropdown(false);
    setActiveIndex(-1);
    focusParent();
  };

  const handleSelect = (
    val: string | number | null | undefined,
    option: IInputOption<T>,
    event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
  ) => {
    if (option.disabled) return;
    if (option.noCta) return;
    handleClose(event);
    if (typeof onChange === 'function') {
      onChange({
        value: String(val ?? ''),
        entity: option.entity,
        event,
        id,
        name,
        key: option.key,
      });
    }
  };

  const renderLabel = () => {
    const found = options.find((o) => o.value === value || o.key === value);
    if (found && typeof valueLabel === 'function') return valueLabel(found);
    if (found) return found.label;
    return '';
  };

  const renderSecondary = () => {
    if (typeof valueSecondary !== 'function') return undefined;
    const found = options.find((o) => o.value === value || o.key === value);
    if (!found) return undefined;
    return valueSecondary(found);
  };

  const renderHeaderDropdown = () => {
    if (minWidth.sm) return null;
    return (
      <div className={s.headerDropdown}>
        <Typo
          text={placeholder ? placeholder : String(label)}
          tag={'h2'}
          size={'df'}
          weight={'medium'}
          color={'text'}
        />
        <ButtonIcon
          classNames={{ button: s.closeDropdown }}
          icon={'close'}
          onClick={({ event }) => handleClose(event)}
          theme={'ghost'}
          size={'db'}
          color={'text'}
        />
      </div>
    );
  };

  const renderIconOption = (option: IInputOption<T>) => {
    if (!option.icon) return null;
    return <Icon className={s.iconOption} name={option.icon} emoji={emoji} />;
  };

  const renderLabelOption = (option: IInputOption<T>) => {
    return (
      <Typo
        text={option.label ?? ''}
        tag={'p'}
        size={'df'}
        lineHeight={'paragraph'}
        color={'text'}
      />
    );
  };

  const renderHintOption = (option: IInputOption<T>) => {
    if (!option.hint) return null;
    const hints = Array.isArray(option.hint) ? option.hint : [option.hint];
    return (
      <Fragment>
        {hints.map((_hint) => (
          <Typo key={_hint} text={_hint} tag={'p'} size={'sm'} color={'text-l'} />
        ))}
      </Fragment>
    );
  };

  const renderContentOption = (option: IInputOption<T>) => {
    if (!option.content) {
      return (
        <div className={s.defaultItem}>
          {renderIconOption(option)}
          <div className={s.wrapLabelOption}>
            {renderLabelOption(option)}
            {renderHintOption(option)}
          </div>
        </div>
      );
    }
    if (typeof option.content === 'function') return option.content();
    return option.content;
  };

  const renderOption = (option: IInputOption<T>, idx: number) => {
    const selected = value && option.value && option.value === value;
    const activeItem = idx === activeIndex;

    return (
      <div
        key={option.key || idx}
        className={cx(s.option, classNames?.option, {
          [s.notHoverOption]: option.notHover,
          [s.notBorderOption]: option.notBorder,
          [s.disabledOption]: option.disabled,
          [s.selected]: selected,
        })}
        ref={(el) => {
          itemsRef.current[option.key] = el;
        }}
        // ref={selected ? itemsRef : undefined}
        // TODO: eliminare option.key da handleSelect quando è sistemato il refactor pannello
        onClick={(event) => handleSelect(option.value ?? option.key, option, event)}
        onKeyDown={(event) =>
          a11yH.handleListNav({
            event: event,
            active: activeIndex,
            total: navigableKeys.length,
            onEsc: handleClose,
            setIndex: setActiveIndex,
            onEnter: () => handleSelect(option.value ?? option.key, option, event),
          })
        }
        onFocus={(event) => {
          const el = event.target;
          if (!el) return;
          const [first] = Array.from(el.children) as HTMLElement[];
          if (!first) return;
          first.focus();
        }}
        role={'option'}
        tabIndex={activeItem ? 0 : -1}
        aria-selected={activeItem}
        aria-disabled={option.disabled}
        {...testId(test, option.key)}
      >
        {renderContentOption(option)}
      </div>
    );
  };

  let motionVariants = {};

  if (minWidth.sm) {
    motionVariants = {
      open: { opacity: 1, height: 'auto' },
      collapsed: { opacity: 0, height: 0 },
    };
  } else {
    motionVariants = {
      open: { opacity: 1, translateY: '0%' },
      collapsed: { opacity: 1, translateY: '100%' },
    };
  }

  const Dropdown = (
    <m.div
      id={dropdownId}
      className={cx(s.dropdown, classNames?.dropdown)}
      style={
        {
          '--w-parent': wParent,
        } as CSSProperties
      }
      initial='collapsed'
      animate='open'
      exit='collapsed'
      variants={motionVariants}
      transition={{
        type: 'tween',
        duration: 0.25,
        ease: [0.215, 0.61, 0.355, 1],
      }}
      role={'listbox'}
      aria-orientation={'vertical'}
      {...testId(test, 'dropdown')}
    >
      {renderHeaderDropdown()}
      <div className={cx(s.bodyDropdown, classNames?.bodyDropdown)} role={'menu'}>
        {options.filter((option) => !option.hidden).map(renderOption)}
      </div>
    </m.div>
  );

  return (
    <Fragment>
      <Input
        ref={parentRef}
        classNames={{
          wrapper: classNames?.wrapper,
          wrapInput: classNames?.wrapInput,
          input: classNames?.input,
        }}
        type={'text'}
        size={size}
        icon={icon}
        label={label}
        hint={hint}
        placeholder={placeholder}
        value={renderLabel()}
        valueSecondary={renderSecondary()}
        error={error}
        errorMsg={errorMsg}
        dropdown
        fakeInput
        required={required}
        loading={loading}
        readOnly={readOnly}
        disabled={disabled}
        onClick={handleOpen}
        onKeyDown={(event) => {
          if (event.key === Keyboard.ENTER) {
            handleOpen();
          }
        }}
        onChange={() => {}}
        a11y={{ role: 'combobox', expanded: showDropdown, controls: dropdownId }}
        test={test}
      />
      <Portal
        mounted={showDropdown}
        content={Dropdown}
        bindTo={parentRef.current}
        onClose={() => setShowDropdown(false)}
        fullscreen={!minWidth.sm}
        clickToHide
        position={'adaptive'}
      />
    </Fragment>
  );
};

export default InputSelect;
